import { getNameToSave } from '@griply/utils';
import React, { forwardRef, useState } from 'react';
import { useForwardedRef } from 'shared/hooks/use-forwarded-ref';
import { useKeysDown } from 'shared/hooks/use-keys-down';

import * as Styled from './editable-text-field.style';

export enum Variants {
  H1 = 'h1',
  H2 = 'h2',
  H4 = 'h4',
  H5 = 'h5',
  Body2 = 'body2',
}

export type EditableTitleProps = {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  variant?: Variants;
  placeholder?: string;
  isStrikeThrough?: boolean;
  allowMultiline?: boolean;
};

export const EditableTextField = forwardRef<
  HTMLInputElement,
  EditableTitleProps
>(
  (
    {
      value,
      onChange,
      disabled,
      variant = Variants.H1,
      placeholder,
      isStrikeThrough,
      allowMultiline,
    },
    ref,
  ) => {
    const [isEditing, setIsEditing] = useState(false);
    const [mountKey, setMountKey] = useState(0);
    const inputRef = useForwardedRef<HTMLInputElement>(ref);

    const _onFocus = () => setIsEditing(true);

    const submitChange = (e: React.FocusEvent<HTMLHeadingElement>) => {
      setIsEditing(false);
      const nameToSave = getNameToSave(e.currentTarget.innerText, value);
      if (!nameToSave) {
        // value did not change, so only a remount puts the stored name back in the edited DOM.
        setMountKey((key) => key + 1);
        return;
      }

      onChange(nameToSave);
    };

    useKeysDown(
      ['Enter'],
      (e) => {
        if (document.activeElement === inputRef.current && !allowMultiline) {
          e.preventDefault();
        }
      },
      { otherKeysInCombinationAllowed: true },
    );

    const handleKeyPress = (e: React.KeyboardEvent) => {
      if (
        document.activeElement === inputRef.current &&
        e.key === 'Enter' &&
        !allowMultiline
      ) {
        e.preventDefault();
        inputRef.current?.blur();
      }
    };

    return (
      <Styled.Typography
        key={mountKey}
        ref={inputRef}
        variant={variant}
        contentEditable={!disabled}
        suppressContentEditableWarning
        onFocus={_onFocus}
        onBlur={submitChange}
        onKeyDown={handleKeyPress}
        $placeholder={placeholder}
        $isStrikeThrough={!isEditing && isStrikeThrough}
      >
        {value}
      </Styled.Typography>
    );
  },
);
